package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import org.w3c.dom.events.EventListener

@Composable
internal fun EditorControllerRuntime(
    state: TranslationEditorState,
    controller: EditorControllerState,
    capturedFocus: EditorCapturedFocusRegistry,
    domBridge: BrowserEditorDomBridge,
    measurementController: EditorMeasurementController,
    applyControllerTransition: (String, EditorTransition) -> Unit,
    updateController: ((EditorControllerState) -> EditorControllerState) -> Unit,
    emitEvidence: (EditorEvidenceEvent) -> Unit,
    sampleQueueScroll: () -> Unit,
    onCompactWorkspaceWidth: (Double) -> Unit,
) {
    val latestController = rememberUpdatedState(controller)
    var nextQueueQueryRevision by remember(state) { mutableStateOf(0L) }
    var initialWorkbenchFocusQueued by remember(state) { mutableStateOf(false) }
    var effectStartCount by remember(state) { mutableStateOf(0) }
    var targetRemeasurementQueuedId by remember(state) { mutableStateOf<Long?>(null) }

    fun abandonLatestMeasurement(
        name: String,
        expectedMeasurementId: Long,
        measurement: EditorWorkbenchMeasurement?,
    ) {
        var before: EditorWorkspaceState? = null
        var transition: EditorTransition? = null
        updateController { current ->
            val abandoned = abandonCurrentEditorMeasurement(
                current = current,
                expectedMeasurementId = expectedMeasurementId,
                contentClientWidth = measurement?.contentClientWidth,
                gutterPx = measurement?.scrollbarGutter ?: 0.0,
            )
            if (abandoned === current) {
                current
            } else {
                before = current.workspace
                transition = EditorTransition(
                    state = abandoned.workspace,
                    effects = abandoned.pendingEffects.drop(current.pendingEffects.size),
                )
                abandoned
            }
        }
        val beforeWorkspace = before ?: return
        val resolvedTransition = transition ?: return
        emitEvidence(
            EditorEvidenceEvents.layoutMeasurementAbandoned(
                expectedMeasurementId,
                measurement != null,
            ),
        )
        emitEvidence(EditorEvidenceEvents.transition(name, beforeWorkspace, resolvedTransition))
    }

    /**
     * @param evidence the evidence to gate on. It is a parameter, not a read of
     *   `latestController.value`, because the probe path records the probe start and accepts the
     *   measurement in the same pass — and `latestController` is a `rememberUpdatedState`, so it
     *   still holds the value from the *previous* composition at that point.
     *
     *   That one-frame gap is what left the editor blank. `recordProbeStart` had run, but the copy
     *   this function read had `probeStartRecorded = false`, so `permitsObserverResult` said no,
     *   `takeBuffered` returned null, and the function returned having done nothing — leaving the
     *   workspace in MEASURING, which paints `visibility: hidden`, permanently. Silent, because
     *   every step in that chain is a legitimate "not yet".
     */
    fun acceptBufferedMeasurement(
        measurementId: Long,
        evidence: EditorPostCommitEvidenceState = latestController.value.postCommitEvidence,
    ) {
        val current = latestController.value
        val workspaceWidth = readEditorWorkspaceClientWidth() ?: return
        val measurement = measurementController.takeBuffered(
            measurementId = measurementId,
            evidence = evidence,
            workspaceClientWidth = workspaceWidth,
        ) ?: return
        measurementController.accept(measurementId, measurement, workspaceWidth)
        applyControllerTransition(
            "layout-measurement-apply",
            current.workspace.applyWorkbenchMeasurement(
                measurementId = measurementId,
                contentClientWidth = measurement.contentClientWidth,
                gutterPx = measurement.scrollbarGutter,
                activeTargetWidth = measurement.activeTargetWidth,
            ),
        )
    }

    fun handleObservedMeasurement(
        observedId: Long,
        measurement: EditorWorkbenchMeasurement?,
    ) {
        val current = latestController.value
        if (observedId != current.workspace.layoutMeasurementId) {
            emitEvidence(
                EditorEvidenceEvents.layoutMeasurementStaleIgnored(
                    staleId = observedId,
                    currentId = current.workspace.layoutMeasurementId,
                ),
            )
            return
        }
        when (current.workspace.measuredLayoutDisposition) {
            EditorMeasuredLayoutDisposition.MEASURING -> {
                measurementController.buffer(observedId, measurement)
                if (current.postCommitEvidence.permitsObserverResult(observedId)) {
                    acceptBufferedMeasurement(observedId)
                }
            }

            EditorMeasuredLayoutDisposition.WORKBENCH -> {
                // **The sidecar's own entrance and exit are not an external resize.**
                //
                // The accepted measurement was taken against the sidecar's *settled* width — the
                // editor's remaining space once the panel is fully open — so while the sidecar's
                // `flex-basis` is animating (`ENTERING`/`EXITING`), the editor is, by design,
                // briefly a different width than that. The `ResizeObserver` this measurement
                // watches fires on every layout-affecting frame of that animation, and treating
                // each one as "the window changed, remeasure" restarted the measurement, and with
                // it the animation itself, over and over — the sidecar never reached `PRESENT`.
                // Once the sidecar settles (`PRESENT`) or the surface is gone (`CLOSED`) its width
                // is stable again, and a real external resize is exactly what this still needs to
                // catch.
                val utilityAnimating = current.workspace.utility != EditorUtilitySurface.NONE &&
                    current.workspace.utilityPresencePhase() in setOf(
                        // Collapsed at `PENDING_ENTER`, mid-transition at `ENTERING`/`EXITING` —
                        // every phase in which the sidecar's own width differs from the settled
                        // width the accepted measurement recorded.
                        EditorUtilityPresencePhase.PENDING_ENTER,
                        EditorUtilityPresencePhase.ENTERING,
                        EditorUtilityPresencePhase.EXITING,
                    )
                if (utilityAnimating) return
                val accepted = measurementController.acceptedMeasurement ?: return
                val currentWorkspaceWidth = readEditorWorkspaceClientWidth() ?: return
                val currentEditorWidth = readEditorScrollerClientWidth() ?: return
                if (measurementHasExternalWidthChange(accepted, currentWorkspaceWidth, currentEditorWidth)) {
                    sampleQueueScroll()
                    val focus = readEditorActiveFocusRef(capturedFocus)
                    val base = current.workspace.copy(
                        focusAfterMeasurement = focus?.let { EditorDeferredFocus(it, null) },
                    )
                    applyControllerTransition(
                        "external-width-invalidation",
                        base.invalidateWorkbenchMeasurement(),
                    )
                }
            }

            EditorMeasuredLayoutDisposition.COMPACT_FALLBACK -> {
                onCompactWorkspaceWidth(readEditorWorkspaceClientWidth() ?: 0.0)
                val accepted = measurementController.acceptedMeasurement ?: return
                val currentWorkspaceWidth = readEditorWorkspaceClientWidth() ?: return
                if (measurementHasExternalWorkspaceWidthChange(accepted, currentWorkspaceWidth)) {
                    if (current.workspace.mountsQueueList()) sampleQueueScroll()
                    val focus = readEditorActiveFocusRef(capturedFocus)
                    val base = current.workspace.copy(
                        focusAfterMeasurement = focus?.let { EditorDeferredFocus(it, null) },
                    )
                    applyControllerTransition(
                        "fallback-width-invalidation",
                        base.invalidateWorkbenchMeasurement(),
                    )
                }
            }

            EditorMeasuredLayoutDisposition.NOT_APPLICABLE -> {
                onCompactWorkspaceWidth(readEditorWorkspaceClientWidth() ?: 0.0)
            }
        }
    }

    LaunchedEffect(state) { state.loadFoundation() }

    val search = state.search
    LaunchedEffect(search) {
        delay(220)
        state.debouncedSearch = search.trim()
    }

    val query = state.currentQuery
    LaunchedEffect(query) {
        nextQueueQueryRevision += 1
        val revision = nextQueueQueryRevision
        applyControllerTransition("query-change", controller.workspace.onQueueQueryChanged(revision))
        state.loadKeys(reset = true, queryRevision = revision)
    }

    LaunchedEffect(state) {
        val focused = readEditorActiveFocusRef(capturedFocus)
        onCompactWorkspaceWidth(readEditorWorkspaceClientWidth() ?: 0.0)
        applyControllerTransition(
            "viewport-initial",
            controller.workspace.withViewportWidth(currentEditorViewportWidth(), focused),
        )
    }

    DisposableEffect(state) {
        val listener = EventListener {
            sampleQueueScroll()
            onCompactWorkspaceWidth(readEditorWorkspaceClientWidth() ?: 0.0)
            val focused = readEditorActiveFocusRef(capturedFocus)
            applyControllerTransition(
                "viewport-resize",
                latestController.value.workspace.withViewportWidth(currentEditorViewportWidth(), focused),
            )
        }
        addEditorResizeListener(listener)
        onDispose { removeEditorResizeListener(listener) }
    }

    DisposableEffect(
        state,
        controller.workspace.layoutMeasurementId,
        controller.workspace.measuredLayoutDisposition,
        state.foundationError,
    ) {
        val disposition = controller.workspace.measuredLayoutDisposition
        if (
            state.foundationError == null &&
            (disposition == EditorMeasuredLayoutDisposition.MEASURING ||
                disposition == EditorMeasuredLayoutDisposition.WORKBENCH ||
                disposition == EditorMeasuredLayoutDisposition.COMPACT_FALLBACK ||
                disposition == EditorMeasuredLayoutDisposition.NOT_APPLICABLE)
        ) {
            measurementController.bind(
                measurementId = controller.workspace.layoutMeasurementId,
                workspace = readEditorWorkspaceElement(),
                editorScroller = readEditorScrollerElement(),
                onMeasurement = ::handleObservedMeasurement,
                onStale = { staleId, _ ->
                    emitEvidence(
                        EditorEvidenceEvents.layoutMeasurementStaleIgnored(
                            staleId = staleId,
                            currentId = latestController.value.workspace.layoutMeasurementId,
                        ),
                    )
                },
            )
        }
        onDispose { measurementController.disconnect() }
    }

    // **A measurement probe must never be able to become the resting state.**
    //
    // While measuring, `EditorComposition` paints the workspace `visibility: hidden` — that is what
    // makes the probe invisible, and it is also what makes a probe that never resolves look exactly
    // like a broken app: a 52px top bar and nothing else, with no error anywhere. That shipped.
    //
    // Every decline in the accept path is individually reasonable ("the id moved on", "no
    // measurement buffered yet", "the DOM hook is not there"), which is precisely why the failure
    // was silent. So the guarantee is made here instead of trusting all of them: if a probe is
    // still running after [EDITOR_MEASUREMENT_TIMEOUT_MS], settle it from whatever the DOM can
    // report and show the user their editor. Worst case that is a stale writing width, which the
    // resize observer corrects on the next real change.
    //
    // The timeout is generous on purpose. `applyWorkbenchMeasurement` may legitimately take a
    // second pass to narrow the side padding before it settles, and this must never pre-empt an
    // ordinary measurement — it is a backstop, not part of the flow.
    LaunchedEffect(
        controller.workspace.layoutMeasurementId,
        controller.workspace.measuredLayoutDisposition,
        controller.workspace.editorSidePaddingPx,
        state.foundationError,
    ) {
        if (state.foundationError != null) return@LaunchedEffect
        if (
            controller.workspace.measuredLayoutDisposition !=
            EditorMeasuredLayoutDisposition.MEASURING
        ) {
            return@LaunchedEffect
        }
        val id = controller.workspace.layoutMeasurementId
        // **A probe that restarts forever looks exactly like a probe that stalls.** Wiring the
        // three-pane layout produced one: the probe laid out two tracks, the settled state laid out
        // three, the resize observer read the difference as an external change and invalidated the
        // measurement — round and round, workspace hidden the whole time. The timeout below never
        // fired, because every restart re-keyed this effect and cancelled the delay.
        //
        // So each unsettled chain is counted as well as timed. Ids are monotonic, so "how many
        // restarts since this chain began" is just a subtraction. A settled result resets the next
        // chain's budget; past the budget within one chain, layout settles from whatever the DOM
        // says rather than measuring forever.
        if (controller.postCommitEvidence.measurementRestartBudgetExceeded(id)) {
            abandonLatestMeasurement(
                name = "layout-measurement-loop-abandon",
                expectedMeasurementId = id,
                measurement = measurementController.readCurrentMeasurement(),
            )
            return@LaunchedEffect
        }
        delay(EDITOR_MEASUREMENT_TIMEOUT_MS)
        val current = latestController.value
        if (
            current.workspace.layoutMeasurementId != id ||
            current.workspace.measuredLayoutDisposition !=
            EditorMeasuredLayoutDisposition.MEASURING
        ) {
            return@LaunchedEffect
        }
        abandonLatestMeasurement(
            name = "layout-measurement-abandon",
            expectedMeasurementId = id,
            measurement = measurementController.readCurrentMeasurement(),
        )
    }

    LaunchedEffect(
        controller.workspace.layoutMeasurementId,
        controller.workspace.measuredLayoutDisposition,
        controller.postCommitEvidence,
        state.foundationError,
    ) {
        if (state.foundationError != null) return@LaunchedEffect
        val workspace = controller.workspace
        val id = workspace.layoutMeasurementId
        when (workspace.measuredLayoutDisposition) {
            EditorMeasuredLayoutDisposition.MEASURING -> {
                if (!controller.postCommitEvidence.probeStartRecorded) {
                    domBridge.awaitAnimationFrame()
                    if (
                        latestController.value.workspace.layoutMeasurementId == id &&
                        latestController.value.workspace.measuredLayoutDisposition ==
                        EditorMeasuredLayoutDisposition.MEASURING &&
                        editorMeasurementCommitIsValid(
                            disposition = EditorMeasuredLayoutDisposition.MEASURING,
                            measurementId = id,
                            acceptedMeasurementId = null,
                            expectedSettledLayoutHook = "measuring",
                        )
                    ) {
                        emitEvidence(
                            EditorEvidenceEvents.layoutMeasurementStart(
                                state = latestController.value.workspace,
                                hooks = readEditorEvidenceHookCounts(),
                                effectStartCount = effectStartCount,
                            ),
                        )
                        val recordedEvidence =
                            latestController.value.postCommitEvidence.recordProbeStart(id)
                        updateController { current ->
                            if (
                                current.workspace.layoutMeasurementId == id &&
                                current.workspace.measuredLayoutDisposition ==
                                EditorMeasuredLayoutDisposition.MEASURING
                            ) {
                                current.copy(
                                    postCommitEvidence = current.postCommitEvidence.recordProbeStart(id),
                                )
                            } else {
                                current
                            }
                        }
                        measurementController.buffer(id, measurementController.readCurrentMeasurement())
                        acceptBufferedMeasurement(id, recordedEvidence)
                    }
                }
            }

            EditorMeasuredLayoutDisposition.WORKBENCH,
            EditorMeasuredLayoutDisposition.COMPACT_FALLBACK,
            -> {
                if (controller.postCommitEvidence.terminalAbandonmentPending) {
                    domBridge.awaitAnimationFrame()
                    val latest = latestController.value
                    if (
                        latest.workspace.layoutMeasurementId == id &&
                        latest.postCommitEvidence.terminalAbandonmentPending &&
                        editorAbandonedFallbackCommitIsValid(
                            disposition = latest.workspace.measuredLayoutDisposition,
                            expectedSettledLayoutHook = latest.workspace.renderedLayout().name.lowercase(),
                        )
                    ) {
                        updateController { current ->
                            recordAbandonedEditorMeasurementCommit(current, id)
                        }
                    }
                } else if (!controller.postCommitEvidence.settledResultRecorded) {
                    domBridge.awaitAnimationFrame()
                    val accepted = measurementController.acceptedMeasurement
                    if (
                        latestController.value.workspace.layoutMeasurementId == id &&
                        editorMeasurementCommitIsValid(
                            disposition = workspace.measuredLayoutDisposition,
                            measurementId = id,
                            acceptedMeasurementId = accepted?.id,
                            expectedSettledLayoutHook = workspace.renderedLayout().name.lowercase(),
                        ) &&
                        accepted != null
                    ) {
                        emitEvidence(
                            EditorEvidenceEvents.layoutMeasurementResult(
                                state = latestController.value.workspace,
                                measurement = accepted.measurement,
                                hooks = readEditorEvidenceHookCounts(),
                                effectStartCount = effectStartCount,
                            ),
                        )
                        updateController { current ->
                            if (current.workspace.layoutMeasurementId == id) {
                                current.copy(
                                    postCommitEvidence = current.postCommitEvidence.recordSettledResult(id),
                                )
                            } else {
                                current
                            }
                        }
                    }
                }
            }

            EditorMeasuredLayoutDisposition.NOT_APPLICABLE -> Unit
        }
    }

    LaunchedEffect(
        state.selectedKeyId,
        state.conflicts[state.selectedKeyId],
        controller.workspace.layoutMeasurementId,
        controller.workspace.measuredLayoutDisposition,
        controller.postCommitEvidence,
    ) {
        val workspace = controller.workspace
        val id = workspace.layoutMeasurementId
        if (
            workspace.measuredLayoutDisposition != EditorMeasuredLayoutDisposition.WORKBENCH &&
            workspace.measuredLayoutDisposition != EditorMeasuredLayoutDisposition.COMPACT_FALLBACK
        ) return@LaunchedEffect
        if (!controller.postCommitEvidence.permitsSettledEffects(id)) return@LaunchedEffect
        // A conflict elsewhere in the queue must not block the row actually on screen from
        // settling — only the selected row's own conflict swaps the fields for ConflictCard here.
        if (
            state.selectedRow == null ||
            state.selectedDraft == null ||
            state.conflicts[state.selectedKeyId] != null
        ) {
            return@LaunchedEffect
        }
        if (targetRemeasurementQueuedId == id) return@LaunchedEffect

        domBridge.awaitAnimationFrame()
        if (
            latestController.value.workspace.layoutMeasurementId != id ||
            latestController.value.workspace.measuredLayoutDisposition != workspace.measuredLayoutDisposition
        ) return@LaunchedEffect
        val accepted = measurementController.acceptedMeasurement ?: return@LaunchedEffect
        val latest = measurementController.readCurrentMeasurement() ?: return@LaunchedEffect
        if (
            accepted.id == id &&
            measurementNeedsTargetRemeasure(
                accepted = accepted,
                activeTargetWidth = latest.activeTargetWidth,
                editableTargetExpected = true,
            )
        ) {
            targetRemeasurementQueuedId = id
            val focus = readEditorActiveFocusRef(capturedFocus)
            val base = latestController.value.workspace.copy(
                focusAfterMeasurement = focus?.let { EditorDeferredFocus(it, null) },
            )
            applyControllerTransition("target-mount-invalidation", base.invalidateWorkbenchMeasurement())
        }
    }

    LaunchedEffect(
        state.selectedKeyId,
        controller.workspace.layoutMeasurementId,
        controller.workspace.measuredLayoutDisposition,
        controller.postCommitEvidence,
    ) {
        if (
            !initialWorkbenchFocusQueued &&
            state.selectedKeyId != null &&
            controller.workspace.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.WORKBENCH &&
            controller.postCommitEvidence.permitsSettledEffects(controller.workspace.layoutMeasurementId)
        ) {
            activeEditorTarget(state)?.let { target ->
                initialWorkbenchFocusQueued = true
                applyControllerTransition("initial-target-focus", controller.workspace.requestFocus(target))
            }
        }
    }

    val runnableEffect = oldestRunnableEditorEffect(
        disposition = controller.workspace.measuredLayoutDisposition,
        layoutMeasurementId = controller.workspace.layoutMeasurementId,
        postCommitEvidence = controller.postCommitEvidence,
        pending = controller.pendingEffects,
    )
    LaunchedEffect(runnableEffect?.id) {
        val effect = runnableEffect ?: return@LaunchedEffect
        effectStartCount += 1
        emitEvidence(EditorEvidenceEvents.effectStart(effect, latestController.value.workspace.layoutMeasurementId))
        val result = try {
            runEditorViewEffect(
                effect = effect,
                environment = BrowserEditorViewEffectEnvironment(capturedFocus, domBridge),
                queueMounted = latestController.value.workspace.mountsQueueList(),
            )
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (cause: Throwable) {
            failedEditorViewEffectResult(effect.id, cause)
        }
        emitEvidence(
            EditorEvidenceEvents.effectComplete(
                effect,
                result,
                latestController.value.workspace.layoutMeasurementId,
            ),
        )
        when (effect) {
            is EditorViewEffect.RestoreQueueScroll -> {
                emitEvidence(
                    EditorEvidenceEvents.scroll(
                        revision = latestController.value.workspace.queueQueryRevision,
                        requestedTopPx = effect.topPx,
                        actualTopPx = readEditorQueueScrollTop() ?: 0.0,
                        effectId = effect.id,
                    ),
                )
            }

            is EditorViewEffect.Focus -> {
                emitEvidence(
                    EditorEvidenceEvents.focus(
                        requested = effect.target,
                        actualId = domBridge.activeElementEvidenceId(),
                        fallbackUsed = result.detail?.startsWith("fallback:") == true,
                        effectId = effect.id,
                    ),
                )
            }
        }
        emitEvidence(
            EditorEvidenceEvents.effectAck(
                effect,
                result,
                latestController.value.workspace.layoutMeasurementId,
            ),
        )
        updateController { current -> acknowledgeEditorControllerEffect(current, result) }
    }

    DisposableEffect(Unit) {
        val restoreOverflow = lockEditorDocumentOverflow()
        onDispose { restoreOverflow() }
    }
}
